import MainStore from './MainStore';
import { RelationshipTable } from './RelationshipTable';
import React, { useEffect, useMemo, useReducer, useState } from 'react';
import Panel from './Panel';
import DropdownButton from './DropdownButton';
import {
  ArrowTopRightOnSquareIcon,
  PencilIcon,
  CheckIcon,
  UserIcon,
} from '@heroicons/react/20/solid'
import { HeroIcon, SolidHeroIcon } from './HeroIcon';
import { EvidenceCard } from './EvidenceCard';
import { DebouncedInput } from './@components/DebouncedInput';
import { Link, useLinkClickHandler, useNavigate } from "react-router-dom";
import EditableResourceField from './EditableResourceField';
import useDataEvent from './CustomHook';
import QuestionCard from './QuestionCard';
import CategoryCard, { CategoryCardPill } from './CategoryCard';
import {  BuildingOffice2Icon,  ChevronRightIcon,  FlagIcon,  LinkIcon,  PlusCircleIcon,  QuestionMarkCircleIcon,  SparklesIcon,  TrashIcon } from '@heroicons/react/24/outline';
import { Bars3Icon } from '@heroicons/react/20/solid';
import ConfirmationPopup from './ConfirmationPopup';
import AIProcessButton from './AIProcessButton';
import PrimitivePicker from './PrimitivePicker';
import { VFImage } from './VFImage';
import { Grid } from  'react-loader-spinner'
import PrimitiveConfig from './PrimitiveConfig';
import { InputPopup } from './InputPopup';
import SummaryCard from './SummaryCard';
import { DescriptionDetails, DescriptionList, DescriptionTerm } from './@components/description-list';
import { useSmoothGradient } from './@components/SmoothGradient';
import { Button, Dropdown, DropdownItem, DropdownMenu, DropdownTrigger } from '@heroui/react';
import RenderItem from './@components/RenderItem';
import clsx from 'clsx';
import { FieldList, buildFieldList, buildPinItems, resolveParameters } from './fieldRendering';
import {
  getEnabledExtensions,
  mergeExtensionParameters,
  buildExtensionActionItems,
} from './extensions';

export const ExpandArrow = function(props) {
  return (
      <svg fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" {...props}>
        <path clipRule="evenodd" fillRule="evenodd" d="M15 3.75a.75.75 0 01.75-.75h4.5a.75.75 0 01.75.75v4.5a.75.75 0 01-1.5 0V5.56l-3.97 3.97a.75.75 0 11-1.06-1.06l3.97-3.97h-2.69a.75.75 0 01-.75-.75zM9.53 14.47A.75.75 0 019.53 15.53L5.56 19.5h2.69a.75.75 0 010 1.5h-4.5a.75.75 0 01-.75-.75v-4.5a.75.75 0 011.5 0v2.69l3.97-3.97a.75.75 0 011.06 0z" />
      </svg>
  
  );
}

let mainstore = MainStore()

  const CardMenu = function({
    primitive,
    custom = [],
    relatedTo,
    callbackProcessor,
    icon,
    title,
    showVisitPage = true,
    showDelete,
    showUnlink,
    showInSidebar,
    placement = 'right',
    buttonProps = {},
    dropdownMenuProps = {},
    dropdownProps = {},
    className,
  }){
    const [showDeletePrompt, setShowDeletePrompt] = React.useState(false)
    const [manualInputPrompt, setManualInputPrompt] = React.useState(false)
    const navigate = useNavigate();

    const handleDelete = ()=>{
      mainstore.removePrimitive( primitive )
      setShowDeletePrompt( null )
    }

    let items = [...custom]

    if( primitive ){
      if( showInSidebar !== false ){
        items.push({
          title: 'Show in sidebar',
          action: ()=>mainstore.sidebarSelect(primitive),
          icon: ArrowTopRightOnSquareIcon,
        })
      }

      if( showVisitPage !== false ){
        items.push({
          title: 'Open page',
          action: ()=>navigate(`/item/${primitive.id}`),
          icon: ArrowTopRightOnSquareIcon,
        })
      }

      items.push({
        title: 'Delete',
        action: ()=>setShowDeletePrompt(true),
        icon: TrashIcon,
        skip: (relatedTo && !((relatedTo && showDelete === 'origin' && primitive.origin.id === relatedTo.id)) || (showDelete === undefined ? false : (showDelete === true)))
      })

      items.push({
        title: `Unlink from ${relatedTo?.displayType}`,
        action: ()=>relatedTo?.removeRelationship(primitive, relatedTo.metadata.isAggregation ? "" : "outcomes"),
        icon: TrashIcon,
        skip: (showUnlink === false || showUnlink === undefined) ? true : (relatedTo === undefined ) || (relatedTo && relatedTo.id === primitive.origin.id) || !(relatedTo && relatedTo?.primitives.includes(primitive))
      })

      if( primitive.metadata?.actions ){
        items = items.concat(
          primitive.metadata.actions
            .filter((d)=>d.menu)
            .map((d)=>{
              return {
                title: d.title,
                icon: d.icon || "PlayIcon",
                action: async ()=>{
                  if( d.manualFields ){
                    setManualInputPrompt({
                      confirm: async (inputs)=>await MainStore().doPrimitiveAction(primitive, d.key, inputs, callbackProcessor),
                    })
                  }else if( d.actionFields ){
                    setManualInputPrompt({
                      primitive: primitive,
                      fields: d.actionFields,
                      confirm: async (inputs)=>await MainStore().doPrimitiveAction(primitive, d.key, inputs, callbackProcessor),
                    })
                  }else{
                    await MainStore().doPrimitiveAction(primitive, d.key, undefined, callbackProcessor)
                  }
                }
              }
            })
        )
      }

      // Inject extension-defined actions when a board context is active
      const activeBoard = mainstore.activeBoard
      if (activeBoard) {
        const extensions = getEnabledExtensions(activeBoard)
        if (extensions.length > 0) {
          const extActions = buildExtensionActionItems(extensions, primitive, mainstore)
          items = items.concat(extActions)
        }
      }
    }

    const menuItems = items.filter((d)=>d && !d.skip)

    const { className: buttonClassNameProp, isIconOnly, startContent, ...restButtonProps } = buttonProps
    const iconOnly = isIconOnly ?? !title
    const {
      placement: dropdownPlacement,
      popoverProps: dropdownPopoverPropsProp = {},
      classNames: dropdownClassNamesProp,
      portalContainer: dropdownPortalContainerProp,
      shouldFlip: dropdownShouldFlipProp,
      shouldCloseOnScroll: dropdownShouldCloseOnScrollProp,
      ...restDropdownProps
    } = dropdownProps

    const resolvedPlacement = dropdownPlacement ?? placement
    const defaultPortalContainer = typeof window !== 'undefined' ? document.body : undefined

    const portalContainer = dropdownPortalContainerProp ?? dropdownPopoverPropsProp.portalContainer ?? defaultPortalContainer
    const shouldFlip = dropdownShouldFlipProp ?? dropdownPopoverPropsProp.shouldFlip ?? true
    const shouldCloseOnScroll = dropdownShouldCloseOnScrollProp ?? dropdownPopoverPropsProp.shouldCloseOnScroll

    const dropdownClassNames = {
      ...dropdownPopoverPropsProp.classNames,
      ...dropdownClassNamesProp,
    }

    dropdownClassNames.content = clsx(
      'max-h-[calc(100vh-4rem)] overflow-y-auto overscroll-contain !flex !flex-col !items-stretch !justify-start',
      dropdownPopoverPropsProp.classNames?.content,
      dropdownClassNamesProp?.content,
    )

    const legacyPopoverRest = { ...dropdownPopoverPropsProp }
    legacyPopoverRest.containerPadding = legacyPopoverRest.containerPadding ?? 32
    delete legacyPopoverRest.classNames
    delete legacyPopoverRest.portalContainer
    delete legacyPopoverRest.shouldFlip
    delete legacyPopoverRest.shouldCloseOnScroll

    const { className: dropdownMenuClassNameProp, ...restDropdownMenuProps } = dropdownMenuProps

    const trigger = (
      <Button
        {...restButtonProps}
        isIconOnly={iconOnly}
        radius={restButtonProps.radius ?? 'sm'}
        variant={restButtonProps.variant ?? (iconOnly ? 'light' : 'flat')}
        size={restButtonProps.size ?? (iconOnly ? 'sm' : 'md')}
        startContent={title && icon ? icon : startContent}
        className={clsx('min-w-0', className, buttonClassNameProp)}
        aria-label={restButtonProps['aria-label'] ?? (!title ? `Open menu for ${primitive?.title ?? 'item'}` : undefined)}
      >
        {title ?? (!title ? (icon ?? <Bars3Icon className='h-5 w-5'/>) : null)}
      </Button>
    )

    return(<>
      {manualInputPrompt && <InputPopup cancel={()=>setManualInputPrompt(false)} {...manualInputPrompt}/>}
      {showDeletePrompt && <ConfirmationPopup message={`This will also delete all items that belong to this ${primitive.displayType}`} title="Confirm deletion" confirm={handleDelete} cancel={()=>setShowDeletePrompt(false)}/>}
      <Dropdown
        placement={resolvedPlacement}
        portalContainer={portalContainer}
        shouldFlip={shouldFlip}
        classNames={dropdownClassNames}
        {...(shouldCloseOnScroll !== undefined ? { shouldCloseOnScroll } : {})}
        {...legacyPopoverRest}
        {...restDropdownProps}
      >
        <DropdownTrigger>
          {trigger}
        </DropdownTrigger>
        <DropdownMenu
          aria-label="Dropdown menu with icons"
          variant="flat"
          className={clsx(dropdownMenuClassNameProp)}
          {...restDropdownMenuProps}
        >
          {menuItems.map((item,idx) => (
            <DropdownItem
              key={item.key ?? idx}
              onPress={item.action ? ()=>{
                item.action()
              } : undefined}
              startContent={
                <>
                  {item.icon && (item.icon.render || item.icon instanceof Function) && <item.icon aria-hidden="true" className='w-6 h-6'/>}
                  {item.icon && typeof(item.icon)==="string" && <HeroIcon icon={item.icon} aria-hidden="true" className='w-6 h-6'/>}
                </>
              }
            >
              {item.title}
            </DropdownItem>
          ))}
        </DropdownMenu>
      </Dropdown>
    </>)

  }


const Relationships = function({primitive, ...props}){
    let temp = primitive.parentPrimitiveRelationships
    let map = primitive.metadata?.relationships 
    if( map == undefined ){return <></>}
    let relationships = Object.keys(map).map((k)=>{
      return {
        key: k,
        items: temp[k] || [],
        ...map[k]
      }
    })
    return <RelationshipTable title={props.title === false ? false : props.title || 'Significance'} relationships={relationships}/>
}
const Users = function({primitive, ...props}){
  const [editing, setEditing] = React.useState(props.editing)
  const rawUsers = primitive?.users
  const userList = Array.isArray(rawUsers)
    ? rawUsers
    : (rawUsers && typeof rawUsers === "object")
        ? Object.values(rawUsers).flat().filter(Boolean)
        : []


  useEffect(()=>{
    setEditing(props.editing)
  }, [primitive.id, props.editing])

  let userContent
  if( props.asTable ){
    userContent = (
      <dl className="mt-2 mx-2 divide-y divide-gray-200 border-t border-b border-gray-200">
        {!editing && userList.length === 0 &&
          <p className='py-3 text-center text-gray-400 text-sm'>Nothing to show</p>
        }
        {userList.map((user)=>(
          <div key={user.email} className="flex justify-between py-3 text-sm relative place-items-center">
            <dt>{user.name}</dt>
              <img
                className="inline-block h-7 w-7 rounded-full right-0 absolute"
                referrerPolicy="no-referrer"
                src={user.avatarUrl}
                alt={user.name} />
          </div>
        ))}
      </dl>
    )
  }else{
    userContent = 
    <div className={`flex space-x-2 ${props.className || 'mt-2 mx-2'}`}>
        {!editing && userList.length === 0 &&
          <p className='w-8 h-8 justify-center place-items-center flex rounded-full text-gray-300 bg-gray-100 border border-gray-300 text-md'><UserIcon/></p>
        }
        {userList.map((user) => (
          <a key={user.email} href={user.href} className="rounded-full hover:opacity-75">
            <img
              className="inline-block h-8 w-8 rounded-full"
              referrerPolicy="no-referrer"
              src={user.avatarUrl}
              alt={user.name} />
          </a>
        ))}
       </div>

  }
  if( props.hideTitle){
    return userContent
  }

  return (<>
    <Panel {...props} title={props.title || "Team"} editToggle={setEditing} editing={editing} hideTitle={props.hideTitle} >
      {userContent}
    </Panel>
  </>)
}

const Banner = function({primitive, ...props}){
  let metadata = primitive.metadata
  return (
    <div className={`flex items-center space-x-2 xs:space-x-5 ${props.className || ""}`}>
      {metadata &&
        <div className="flex-shrink-0">
          {metadata && <div className="relative">
              <HeroIcon icon={metadata.icon} className={`${props.small ? "w-12 h-12" : "w-12 h-12 md:w-20 md:h-20"}`}/>
          </div>}
        </div>
      }
      <div className='flex-grow w-full'>
        <h1 className={`flex place-items-center text-lg ${!props.small && "md:text-2xl"} font-bold text-gray-900`}><p className='hidden xs:inline'>#{primitive.plainId} {primitive.title}</p>
          {props.showLink &&
            <button
                type="button"
                className="ml-1 flex h-5 w-5 flex-none items-center justify-center rounded-full bg-white text-gray-400 hover:bg-gray-100 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <Link to={`/item/${primitive.id}`}><ArrowTopRightOnSquareIcon className="h-4 w-4" aria-hidden="true" /></Link>
            </button>}
        </h1>
        {!props.small && metadata && <div className="text-xs md:text-sm font-medium text-gray-500">{metadata.title}<p className='hidden xs:inline'> - {metadata.description}</p></div>}
      </div>
      {props.showMenu &&
        <CardMenu
          primitive={primitive}
          showVisitPage={false}
          buttonProps={{ variant: 'light', isIconOnly: true, size: 'md' }}
        />
      }
      {props.showStateAction &&
              <DropdownButton colorKey='colorBase' items={mainstore.stateInfo[primitive.type]} selected={primitive.state}/>
      }
    </div>
  )
}

const CompactBanner = function(props) {
  return (
    <Banner
      {...props}
      small
    />
  )
}

 

const Parameters = function({primitive, ...props}){

  const [eventTracker, updateForEvent] = React.useReducer( (x)=>x+1, 0)
  const callbackId = React.useRef(null)

  React.useEffect(()=>{
    if( !props.noEvents ){
      callbackId.current = mainstore.registerCallback(callbackId.current, "parameter_update set_parameter", updateForEvent, primitive.id )
      return ()=>{
        mainstore.deregisterCallback(callbackId.current )
      }
    }
  }, [])

  const parameters = resolveParameters({
    primitive,
    activeParameters: props.activeParameters,
    showParents: props.showParents,
    metadataFallback: PrimitiveConfig.metadata,
  })

  // Merge extension parameters when a board context is active
  const activeBoard = mainstore.activeBoard
  const effectiveParams = React.useMemo(() => {
    if (!activeBoard) return parameters
    const extensions = getEnabledExtensions(activeBoard)
    if (extensions.length === 0) return parameters
    return mergeExtensionParameters(parameters, extensions, primitive)
  }, [activeBoard?.id, parameters, primitive?.referenceId])

  const source = primitive.referenceParameters
  const { items } = buildFieldList({
    primitive,
    parameters: effectiveParams,
    source,
    fieldsOverride: props.fields,
    hidden: props.hidden,
    includeAutoParam: primitive.metadata?.autoParam,
    fullList: props.fullList,
    showExtra: props.showExtra,
    editing: props.editing,
  })

  if (!items.length) {
    return null
  }

  const mode = props.mode ?? "form"
  const density = props.density ?? "md"
  const showLabel = props.showLabel ?? true
  const potentialTarget = fieldsBeingProcessed(primitive)

  return (
    <FieldList
      items={items}
      primitive={primitive}
      itemsSource={props.items}
      editing={props.editing}
      editable={props.editable}
      mode={mode}
      density={density}
      showLabel={showLabel}
      includeTitle={props.includeTitle}
      leftAlign={props.leftAlign}
      className={props.className}
      potentialTarget={potentialTarget}
      renderItem={RenderItem}
    />
  )

}
const fieldsBeingProcessed = function(primitive){

  const checkSection = ( section)=>{
    if( !section || !(section instanceof Object)){return []}
      const temp = Object.values(section).filter((d)=>{
        if( d && d.targetFields ){
          if( d.started ){
            if( (new Date() - new Date(d.started)) > (5 * 60 *1000) ){
              return false
            }
          }
          return true
        }
        return false
      })
      return temp.reduce((o,a)=>{o = o.concat(a.targetFields);return o}, [])
  }

  if( primitive  && primitive.processing){
    return [
      checkSection( primitive.processing.ai),
      checkSection( primitive.processing),
    ].flat()
  }
}

const EvidenceList = function({primitive, ...props}){
  let evidence = props.evidenceList || primitive?.primitives.allUniqueEvidence
  useDataEvent('relationship_update', evidence.map((d)=>d.id))
  if( evidence === undefined || evidence === null || evidence.length === 0){return <></>} 


  let categoryIds = evidence.map((d)=>d.referenceId)
  if( primitive?.metadata?.evidenceCategories ){
    categoryIds = categoryIds.concat(primitive.metadata.evidenceCategories)
  }
  categoryIds = categoryIds.filter((v,i,a)=>v && a.indexOf(v)==i)
  let evidenceCategories = categoryIds.map((id)=>mainstore.category(id))

  let evidenceGroups = evidence.reduce((o, d)=>{
      let c = d._packed ? d.primitive : d
      if( c.metadata ){
        let evidenceType = c.metadata.id
        
        o[evidenceType] = o[evidenceType] || []
        o[evidenceType].push( d )
      }
      return o
    },{})

    if( evidenceCategories.length == 0 && Object.values(evidenceGroups).length === 0){
      return <></>
    }

  return (
         <div className={props.className || ""}>
         {!props.hideTitle && 
          <h3 className="mt-6 text-sm font-medium text-gray-500">{props.title || "Evidence"}</h3>
         }
         <div className='flex flex-col mt-1 overflow-y-scroll max-h-[inherit]'>
          {evidenceCategories.map((e)=>
            <div key={e.id} className='mx-1 my-0.5 p-1 bg-gray-50' >
              <p className='p-0.5 text-xs uppercase text-gray-500 w-full flex place-items-center mt-1 ml-0'><HeroIcon icon={e.icon} className='w-5 h-5 mr-1'/>{e.title}</p>
              {(evidenceGroups[e.id] === undefined || evidenceGroups[e.id].length === 0)
                && <p className='text-sm p-2 min-h-[3em] '>No items</p>
              }
              {(evidenceGroups[e.id] &&  evidenceGroups[e.id].length > 0) && 
                <div className={`p-2 w-full gap-3 ${props.frameClassName || ""} space-y-3 no-break-children`}>
                  {evidenceGroups[e.id].map((item)=>{
                    let origin
                    if( item._packed ){
                      origin = item.origin
                      item = item.primitive
                    }
                    return <PrimitiveCard onClick={props.onCardClick ? ()=>props.onCardClick(item) : undefined} key={item.id} showSidebar primitive={item} compact={true} border={true} origin={props.showOriginInfo && (origin || item.origin)} showOriginInfo={props.showOriginInfo} relationshipTo={props.relationshipTo || primitive} relationshipMode={props.relationshipMode} relationshipPath='outcomes' showCategories={props.showCategories} fields={props.cardFields}/>
                  })}
                </div>
              }
            </div>
          )}
         </div>
        </div>
  )
}


const Evidence = function({primitive, ...props}){
  let evidence = props.evidenceList || primitive?.primitives.allUniqueEvidence
  if( evidence === undefined || evidence === null || evidence.length === 0){return <></>} 

  if( props.aggregate ){
    evidence = Object.values(evidence.reduce((o, c)=>{
      if( c.metadata ){

        let evidenceType = c.metadata.id
        
        o[evidenceType] = o[evidenceType] || {e:c , count: 0}
        o[evidenceType].count++
      }

      return o
    },{}))
  }else{
    evidence = evidence.map((e)=>({e:e, count: 1}))
  }

  return (
         <div>
         {!props.hideTitle && 
          <h3 className="mt-6 text-sm font-medium text-gray-500">{props.title || "Evidence"}</h3>
         }
         <div>
          {evidence.map((e)=><EvidenceCard key={e.e.id} evidence={e.e} count={e.count} asPill={true}/>)}
         </div>
        </div>
  )
}

const Details = function({primitive, ...props}){
  const [editing, setEditing] = React.useState(props.editing)

  useEffect(()=>{
    setEditing(props.editing)
  }, [primitive.id, props.editing])
  
  const eventTracker = useDataEvent(props.noEvents ? undefined : ["set_parameter","set_field"], primitive.id )
  const metadata = primitive.metadata
  const parameters = primitive.metadata?.parameters
  const showParents = primitive.origin?.childParameters || primitive.task?.itemParameters?.[primitive.referenceId]

  // Resolve extension parameters when a board context is active
  const activeBoard = mainstore.activeBoard
  const extensionMergedParams = React.useMemo(() => {
    if (!activeBoard) return parameters
    const extensions = getEnabledExtensions(activeBoard)
    if (extensions.length === 0) return parameters
    return mergeExtensionParameters(parameters, extensions, primitive)
  }, [activeBoard?.id, parameters, primitive?.referenceId])

  const effectiveParams = extensionMergedParams ?? parameters

  if( !effectiveParams && !primitive.parentParameters ){ return <></> }
  
  const panelTitle = <>{props.title || "Details"}{metadata.do_discovery && <AIProcessButton active="document_discovery" primitive={primitive} process={(p)=>p.analyzer().doDiscovery({force: true})}/>}</>

  const baseItems = buildFieldList({
    primitive,
    parameters: effectiveParams,
    source: primitive.referenceParameters,
    includeAutoParam: primitive.metadata?.autoParam,
    fullList: editing ?? props.fullList,
    showExtra: props.showExtra,
    editing,
  }).items

    const parentItems = showParents
    ? buildFieldList({
        primitive,
        parameters: resolveParameters({ primitive, showParents: true, metadataFallback: PrimitiveConfig.metadata }),
        source: primitive.referenceParameters,
        includeAutoParam: false,
        fullList: editing,
        showExtra: props.showExtra,
        editing,
      }).items
    : []

  const mode = props.mode ?? "form"
  const density = props.density ?? "md"
  const showLabel = props.showLabel ?? true
  const potentialTarget = fieldsBeingProcessed(primitive)
  
  return (
        <Panel title={panelTitle} editToggle={setEditing} editing={editing} hideTitle={props.hideTitle} {...props} >
          <dl className={`mt-2 mx-2 divide-y divide-gray-200 ${props.hideTitle ? "" : "border-t"} border-b border-gray-200 relative py-2`}>
            <FieldList
              items={baseItems}
              primitive={primitive}
              itemsSource={props.items}
              editing
              editable={true}
              mode={mode}
              density={density}
              showLabel={showLabel}
              includeTitle={props.includeTitle}
              leftAlign={props.leftAlign}
              className={props.className}
              potentialTarget={potentialTarget}
              renderItem={RenderItem}
            />
            {parentItems.length > 0 && (
              <FieldList
                items={parentItems}
                primitive={primitive}
                itemsSource={props.items}
                editing
                editable={true}
                mode={mode}
                density={density}
                showLabel={showLabel}
                includeTitle={false}
                leftAlign={props.leftAlign}
                className={props.className}
                potentialTarget={potentialTarget}
                renderItem={RenderItem}
              />
            )}
          </dl>
          {!props.hideFooter && 
            <h3 className={`flex text-slate-400 font-medium tracking-tight text-xs uppercase mt-2 place-items-center justify-end mt-2`}>
              {metadata.icon && <HeroIcon icon={metadata.icon} className='w-5 h-5 mr-1' strokeWidth={1}/>}
              {metadata.description}
            </h3>
            }
        </Panel>
  )
}

const Title = function({primitive, ...props}){
  const [showParentLinksManager, setShowParentLinksManager ] = useState(false)
  let color = primitive?.stateInfo?.colorBase || "gray"
  let relationshipConfig
  let relationship
  let metadataRender
  if(!primitive)return <></>
  
  if( props.relationshipTo ){
    relationship = primitive.parentRelationship(props.relationshipTo)
    relationshipConfig = (props.relationships && props.relationships[ relationship ]) || {title: relationship, color: 'gray'}
  }
  if( props.relationship ){
    relationshipConfig =  (props.relationships && props.relationships[ props.relationship ]) || {title: props.relationship, color: 'gray'}
  }

  let relationshipRender
  if( props.relationshipMode === "none"){
  }
  else if( props.relationshipMode === "presence"){
    const manageParentLinks = ()=>{
      setShowParentLinksManager(true)
      
    }
    if( relationship ){
      relationshipRender = <HeroIcon onClick={manageParentLinks} icon='StarIcon' className='ml-auto w-6 h-6 stroke-width-[0.5px] text-ccgreen-600 hover:text-ccgreen-900 fill-ccgreen-300 hover:fill-ccgreen-400'/>
    }else{
      if( props.relationshipTo && props.relationshipTo.primitives.allUniqueEvidence.filter((d)=>d.metadata?.isAggregation && d.primitives.descendantsInclude(primitive.id)).length > 0){
        relationshipRender = <HeroIcon onClick={manageParentLinks} icon='StarIcon' className='ml-auto w-6 h-6 stroke-width-[0.5px] text-ccpurple-600 hover:text-ccpurple-900 fill-ccpurple-300 hover:fill-ccpurple-400'/>
      }else{
        relationshipRender = <HeroIcon onClick={manageParentLinks} icon='StarIcon' className='ml-auto w-6 h-6 text-gray-300 hover:text-gray-600'/>
      }
    }
  }else{
    if( relationshipConfig ){
      relationshipRender = <span className={`inline-flex items-center rounded-full bg-${relationshipConfig.color}-100 px-2 py-0.5 text-xs font-medium text-${relationshipConfig.color}-800 ml-auto`}>
        {relationshipConfig.title}
        {relationshipConfig.icon && <HeroIcon icon={relationshipConfig.icon} className='w-4 h-4'/>}
      </span>
    }
  }
  if( props.showMetadataTitle && primitive.metadata){
    let metadata = primitive.metadata
    metadataRender = props.showMetadataTitle === "full"
      ? <p className='ml-1 truncate'>{metadata.title}</p>
      : <p className='ml-1 truncate opacity-0 group-hover:opacity-75 transition-opacity'>· {metadata.title}</p>
  }


  return (
    <>
    {showParentLinksManager && <PrimitivePicker.ParentLinksManager primitive={primitive} rootTitle={`Related ${props.relationshipTo.displayType}`} includeRoot={true} setOpen={()=>setShowParentLinksManager(false)} type="evidence" root={props.relationshipTo}/>}
      <h3 className={`flex text-slate-400 font-medium tracking-tight place-items-center text-${props.compact ? 'xs' : 'sm'} ${props.className}`}>
        {(props.showId === undefined || props.showId === true) && <p>{primitive.displayType} #{primitive.plainId}</p>}
        {(props.showId === "number") && <p>#{primitive.plainId}</p>}
        {props.showState && primitive.stateInfo.title && 
          <span className={`inline-flex items-center rounded-full bg-${color}-100 px-2 py-0.5 text-xs font-medium text-${color}-800 ml-3`}>
            {primitive.stateInfo.title}
        </span>
        }
        {relationshipRender}
        {metadataRender}
        {(props.compact && props.showLink) &&
          <button
              type="button"
              className="ml-1 flex h-5 w-5 flex-none items-center justify-center rounded-full bg-white text-gray-400 hover:bg-gray-100 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            {props.showLink && <Link to={`/item/${primitive.id}`}><ArrowTopRightOnSquareIcon className="h-4 w-4" aria-hidden="true" /></Link>}
          </button>
        }
      </h3>
    </>
  )
}

const Hero = function({primitive, ...props}){
    const metadata = primitive.metadata
    const color = primitive.workspace?.color || "slate"
    const {gradient, values: colors} = useSmoothGradient(color)
    const navigate = useNavigate();

    return (<div 
        onClick={(e)=>{
          navigate(`/item/${primitive.id}`)
        }}
        tabIndex='0'
        id={primitive.plainId}
        className={
        [
          "pcard group relative flex flex-col space-between",
          props.bg ? props.bg : 'bg-content1 text-foreground',
          'w-96 min-h-[12em]',
          'rounded-lg',
          `focus:ring-2 ring-offset-4 focus:outline-none hover:ring-2 hover:ring-ccgreen-300 ${props.dragShadow ? "" : "hover:subtle-shadow-bottom"}`,
          "shadow border border-default-200",
          props.className].filter((d)=>d).join(' ')
        }>
          <div className={`relative h-32 rounded-t-lg flex-0 text-white`}>
            {/*<div className={`absolute pattern-isometric pattern-${color}-600 pattern-bg-${color}-500 pattern-opacity-20 pattern-size-8 h-full w-full rounded-t-lg`}></div>*/}
            <div className={`absolute h-full w-full rounded-t-lg`} style={{backgroundImage: gradient}}></div>
            <div className='absolute bottom-0 px-3 py-2 flex w-full'>
              {metadata && metadata.icon && <HeroIcon icon={metadata.icon} className='w-16 h-16' strokeWidth={0.8}/>}
              <div className='ml-2'>
                {metadata && <p className='text-2xl font-light'>{metadata.title}</p>}
                {metadata && <p className='text-lg font-light'>{metadata.description}</p>}
              </div>
            </div>
          </div>
          <CardMenu
            primitive={primitive}
            className='absolute right-2 top-2'
            buttonProps={{ variant: 'light', isIconOnly: true }}
          />
          <p className='px-4 py-2 text-default-700 text-lg my-2 flex-1'>{primitive.title}</p>
          <Users primitive={primitive} hideTitle={true} className='px-4'/>
          <Title primitive={primitive} showState={true} className='px-4 py-4 flex-0'/>
        </div>)
}
const Categories = function({primitive, ...props}){
  const [update, forceUpdate] = useReducer( (x)=>x+1, 0)
  useDataEvent("set_field relationship_update", [props.relatedTo?.id, primitive.id], forceUpdate)

  const createCategory = async (referenceId)=>{
    const newPrim = await MainStore().createPrimitive({type: 'category', parent: primitive, categoryId: referenceId})
  }
    
  let button
  const baseCategories = [90, 166]

  let createButtons = [
                          {title:"Create new", small:true, action: ()=>createCategory()},
                          //{title:"As Axis", small:true, action: ()=>MainStore().doPrimitiveAction(primitive, "define_axis")}
                      ]
  for(const d of baseCategories){
    const category = mainstore.category(d)
    if( category){
      createButtons.push({title:`New ${category.title}`,small:true,  action: ()=>createCategory(d)})
      
    }
  }

  let list = [
    ...(props.directOnly ? primitive.primitives.origin.allUniqueCategory : primitive.primitives.allUniqueCategory),
    ...(primitive.findParentPrimitives({type: ["board", "flow"]}).flatMap(d=>d.primitives.origin.allCategory))
  ].filter(Boolean)
  
  if( !props.includeResult ){
    if( primitive.metadata?.resultCategories ){
      const excludeIds = primitive.primitives.results.uniqueAllIds
      list = list.filter((d)=>!excludeIds.includes(d.id))
    }
  }

  const content = <>
      <dd className="mt-1 text-sm text-gray-900">
        <ul role="list" className="divide-y divide-gray-200 rounded-md border border-gray-200 space-y-2">
          {(list === undefined || list.length === 0) && 
            <div className='w-full p-2'>
              <button
              type="button"
              onClick={props.editable ? createCategory : undefined}
              className="relative block w-full rounded-lg border-2 border-dashed border-gray-300 p-12 text-center hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
            >
              <span className="mt-2 block text-sm font-semibold text-gray-900">{props.editable ? 'Create a new Category' : 'Nothing to show'}</span>
            </button>
            </div>
          }
          {list.map((category, idx) => <CategoryCard key={category.id} primitive={category} parentView={primitive} {...props} disableHover/>)}
        </ul>
      </dd>
  </>

  return props.hidePanel 
    ? 
      <div class={props.className ?? 'w-full h-full relative'}>
        {content}
        <div className='absolute right-6 bottom-2'>
          <DropdownButton flat noBorder icon={<PlusCircleIcon className='w-5 h-5'/>} items={createButtons} portal placement='right-end'/>
        </div>
      </div>
    : <Panel key='analysis' title={(<>Categories{button}</>)} collapsable={true} open={props.panelOpen !== undefined ? props.panelOpen : list && list.length > 0 } titleButton={createButtons} titleClassName='w-full font-medium text-sm text-gray-500 pt-5 pb-2 flex place-items-center'>
      {content}
    </Panel>
  
}

const Questions = function({primitive, ...props}){
  const [update, forceUpdate] = useReducer( (x)=>x+1, 0)
  useDataEvent("set_field relationship_update", [props.relatedTo?.id, primitive.id], forceUpdate)
  let aiProcessSummary
  let analyzer
  let promptCategories

  if(false && props.relatedTo ){
    if(props.relatedTo.analyzer){
      analyzer = props.relatedTo.analyzer()
      if(analyzer.aiProcessSummary){
        aiProcessSummary = analyzer.aiProcessSummary() 
      }
    }
  }
  const resultTypes = primitive.metadata?.resultCategories?.map((d)=>d.resultCategoryId) || []
  console.log(resultTypes)
  promptCategories = resultTypes.map((id)=>{
    return mainstore.category(id).promptCategories
  }).flat().filter((v,i,a)=>v && a.indexOf(v)==i)

  const createQuestion = async ()=>{
    const newPrim = await MainStore().createPrimitive({type: 'question', parent: primitive})
  }
    
  let button
  
  if( props.relatedTo && props.relatedTo !== primitive ){
    button = <AIProcessButton active="document_questions" primitive={props.relatedTo} process={(p)=>p.analyzer().analyzeQuestions()}/>
  }

  const list = primitive.primitives.allQuestion

  return (
    <Panel key='analysis' title={(<>Questions{button}</>)} collapsable={true} open={props.panelOpen !== undefined ? props.panelOpen : list && list.length > 0} titleButton={{title:'Create new',small:true,action: createQuestion}} titleClassName='w-full font-medium text-sm text-gray-500 pt-5 pb-2 flex place-items-center'>
      <dd className="mt-1 text-sm text-gray-900">
        <ul role="list" className="divide-y divide-gray-200 rounded-md border border-gray-200">
          {(list === undefined || list.length === 0) && 
            <div className='w-full p-2'>
              <button
              type="button"
              onClick={props.editable ? createQuestion : undefined}
              className="relative block w-full rounded-lg border-2 border-dashed border-gray-300 p-12 text-center hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
            >
              <span className="mt-2 block text-sm font-semibold text-gray-900">{props.editable ? 'Create a new Question' : 'Nothing to show'}</span>
            </button>
            </div>
          }
          {list.map((question, idx) => (
            <QuestionCard key={question.id} promptCategories={promptCategories} primitive={question} {...props} aiProcessSummary={aiProcessSummary}/>
          ))}
        </ul>
      </dd>
    </Panel>
  )
}

const Entity=({primitive, ...props})=>{
  let ring = !props.disableHover
  let margin = props.bigMargin ? (ring ? 'px-4 py-6' : 'px-2 py-3') : (ring ? 'px-2 py-3' : 'px-0.5 py-1')
  let mainTextSize = props.textSize || (props.compact ? 'sm' : 'md' )

  const handleEnter = (e)=>{
    if( e.key === "Enter"){
      e.preventDefault();
      e.stopPropagation()
      props.onEnter()
    }
  }

  const bgImg =  primitive?.referenceParameters?.hasBgImg ? true : primitive.linkedInData ? primitive.linkedInData.background_cover_image_url : undefined
  const logoImg = primitive?.referenceParameters?.hasImg ? true : primitive.linkedInData ? primitive.linkedInData.profile_pic_url : undefined

  if(props.micro){
    return <div 
          onClick={props.onClick ? (e)=>{props.onClick(e, primitive)} : undefined}
          onKeyDown={props.onEnter ? handleEnter : undefined}
          tabIndex='0'
          style={props.style}
          id={primitive.id}
          className={
          [
            "pcard group relative flex rounded-lg p-2 bg-content1 text-foreground",
            ring ? `focus:ring-2 focus:outline-none hover:ring-1 hover:ring-ccgreen-300 ${props.dragShadow ? "" : "hover:subtle-shadow-bottom"}` : '',
            'place-items-center',
            props.className].filter((d)=>d).join(' ')
          }>

          { logoImg && (logoImg !== null) &&
            <VFImage className="w-8 h-8 mr-2 object-contain my-auto" src={`/api/image/${primitive.id}${primitive.imageCount ? `?${primitive.imageCount}` : ""}`} />
            }
            <p className='text-sm'>{primitive.title}</p>

          </div>
  }

  let content 
  let header
  let buttonSize = 5

  if( props.imageOnly || (props.fixedSize && props.scale < 0.4) ){
    
      content = logoImg ? <VFImage className={`${props.compact ? "p-0.5" : "p-4"} min-w-[2rem] min-h-[2rem] w-full h-full object-contain m-auto`} src={`/api/image/${primitive.id}${primitive.imageCount ? `?${primitive.imageCount}` : ""}`} /> : <BuildingOffice2Icon className='text-gray-500 p-4'/>
       
      buttonSize = props.compact ? 5 : 16
  }else if(props.fullCard){
    const offerings = props.showOfferings && primitive.referenceParameters.offerings?.split(',').map(d=>d.trim().replace(/^and\s+/i, '')).map(d=>d.charAt(0).toUpperCase() + d.slice(1))
    const customers = props.showCustomers && primitive.referenceParameters.customers?.split(',').map(d=>d.trim().replace(/^and\s+/i, '')).map(d=>d.charAt(0).toUpperCase() + d.slice(1))

    let middle = [
      offerings &&  <div className='flex flex-col w-full border p-2 rounded-md min-h-[8rem]'>
        <p className='font-semibold text-xs'>Offerings</p>
        {offerings.map(d=><p>{d}</p>)}
      </div>,
      customers && <div className='flex flex-col w-full border p-2 rounded-md min-h-[8rem]'>
        <p className='font-semibold text-xs'>Target Customers</p>
        {customers.map(d=><p>{d}</p>)}
      </div>
    ]
    header = <>
          {props.hideCover !== true && bgImg && (bgImg !== null) &&
              <VFImage className="object-cover h-24 w-full rounded-t-lg" src={`/api/image/${primitive.id}-background`}/>
          }
          {props.hideCover !== true && (!bgImg || (bgImg === null)) &&
              <div className="min-h-[4rem] w-full rounded-t-lg bg-gray-300"/>
          }
          {!props.hideTitle && <div className={`px-4 py-1 ${props.hideCover !== true ? "bg-gray-800/50 absolute top-0 left-0 text-white " : "text-gray-800"} rounded-t-lg w-full`}>
              <p className='text-sm'>{primitive.displayType} #{primitive.plainId}</p>
            </div>}
           </>
      content = <>
        <div className='w-full px-4 pt-2 flex place-items-center'>
          { logoImg && (logoImg !== null) &&
            <VFImage className="w-8 h-8 object-contain my-auto" src={`/api/image/${primitive.id}${primitive.imageCount ? `?${primitive.imageCount}` : ""}`} />
            }
            <p className={`${props.fixedSize ? "line-clamp-2" : "py-2"} px-2 text-lg text-gray-700 font-semibold`}>{primitive.title}</p>
          </div>
          {props.hideDescription !== true && 
            <div className='grow'>
              <p className={`${props.fixedSize ? "line-clamp-4" : "py-2"} text-gray-500 px-4 text-sm `}>
                {primitive.referenceParameters.description}
            </p>
          </div>
          }
          {middle && <div className='flex gap-2 font-light m-4 text-gray-600 text-sm'>{middle}</div>}
          {primitive.referenceParameters?.url && 
            <a 
              target='_blank'
              href={primitive.referenceParameters.url}
              className='text-gray-300 hover:text-gray-600 px-4 py-2 mt-1 text-xs font-semibold flex'>
              <LinkIcon className='h-4 pr-0.5'/><p className='truncate'>{props.urlShort ? "Link" : primitive.referenceParameters.url}</p>
            </a>}
        </>

  }else{
    header = <>
          {props.hideCover !== true && bgImg && (bgImg !== null) &&
              <VFImage className="object-cover h-24 w-full rounded-t-lg" src={`/api/image/${primitive.id}-background`}/>
          }
          {props.hideCover !== true && (!bgImg || (bgImg === null)) &&
              <div className="min-h-[4rem] w-full rounded-t-lg bg-gray-300"/>
          }
          {!props.hideTitle && <div className={`px-4 py-1 ${props.hideCover !== true ? "bg-gray-800/50 absolute top-0 left-0 text-white " : "text-gray-800"} rounded-t-lg w-full`}>
              <p className='text-sm'>{primitive.displayType} #{primitive.plainId}</p>
            </div>}
           </>

      content = <>
        <div className='w-full px-4 pt-2 flex place-items-center'>
          { logoImg && (logoImg !== null) &&
            <VFImage className="w-8 h-8 object-contain my-auto" src={`/api/image/${primitive.id}${primitive.imageCount ? `?${primitive.imageCount}` : ""}`} />
            }
            <p className={`${props.fixedSize ? "line-clamp-2" : "py-2"} px-2 text-lg text-gray-700 font-semibold`}>{primitive.title}</p>
          </div>
          {props.hideDescription !== true && 
            <div className='grow'>
              <p className={`${props.fixedSize ? "line-clamp-4" : "py-2"} text-gray-500 px-4 text-sm `}>
                {primitive.referenceParameters.description}
            </p>
          </div>
          }
          {(props.showCategories && props.hideCategories !== true) && <div className='w-full px-4 flex flex-wrap'>
            {primitive.categories.map((category)=>(
              <CategoryCardPill key={category.id} primitive={category}/>
            ))}
          </div>}
          {primitive.referenceParameters?.url && 
            <a 
              target='_blank'
              href={primitive.referenceParameters.url}
              className='text-gray-300 hover:text-gray-600 px-4 py-2 mt-1 text-xs font-semibold flex'>
              <LinkIcon className='h-4 pr-0.5'/><p className='truncate'>{props.urlShort ? "Link" : primitive.referenceParameters.url}</p>
            </a>}
        </>
  }

  let style = {}
  if( props.fixedWidth){
    style.minWidth = props.fixedWidth
    style.maxWidth = props.fixedWidth
  }
  if( props.fixedSize || props.imageOnly){
    style.minWidth = props.fixedSize || "4rem"
    style.maxWidth = props.fixedSize || "4rem"
    style.minHeight = props.fixedSize || "4rem"
    style.maxHeight = props.fixedSize || "4rem"
  }
  
  const showAsProcessing = primitive.processing?.pivot

  const menuButtonSize = buttonSize >= 12 ? 'lg' : buttonSize > 6 ? 'md' : 'sm'

  return (
    <div 
        onClick={props.onClick ? (e)=>{props.onClick(e, primitive)} : undefined}
        onKeyDown={props.onEnter ? handleEnter : undefined}
        tabIndex='0'
        id={props.fullId ? primitive.id : primitive.plainId}
        style={style}
        className={
        [
          "pcard group relative flex flex-col ",
          props.hideCover !== true ? "min-h-[12rem]" : "", 
          props.bg ? props.bg : 'bg-content1 text-foreground',
          props.flatBorder ? '' : 'rounded-lg',
          ring ? `focus:ring-2 focus:outline-none hover:ring-1 hover:ring-ccgreen-300 ${props.dragShadow ? "" : "hover:subtle-shadow-bottom"}` : '',
          props.border ? "shadow border border-default-200" : '',
          props.inline ? "flex space-x-2" : "",
          props.dragShadow ? "shadow-xl rotate-[-5deg]" : "",
          props.className].filter((d)=>d).join(' ')
        }>
          {header}
          {!props.hideMenu && <CardMenu
            primitive={primitive}
            callbackProcessor={props.callbackProcessor}
            className='absolute right-1 top-1'
            showVisitPage={false}
            buttonProps={{
              variant: 'light',
              isIconOnly: true,
              size: menuButtonSize,
              className: 'bg-white/50 group-hover:bg-white'
            }}
            custom={[
              {
                title: 'Expand',
                action: ()=>props.onEnter ? props.onEnter(primitive) : undefined,
                icon: ExpandArrow,
              },
            ]}
            />}
            {content}
          {showAsProcessing && ProcessingPane()}
    </div>
  )
}

const Variant=({primitive, ...props})=>{
  if( primitive === undefined){return}
  const type = props.listType || primitive.type
  if( type === 'prompt' ){
    return <Prompt primitive={primitive} {...props}/>
  }
  if( type === 'entity' ){
    return <Entity primitive={primitive} {...props}/>
  }
  if( type === 'category' ){
    return <CategoryCard primitive={primitive} {...props}/>
  }
  if( type === 'summary' ){
    return <SummaryCard primitive={primitive} {...props}/>
  }
  if( type === 'category_pill' ){
    return <CategoryCard.Pill primitive={primitive} {...props}/>
  }
  return undefined
}
export const Prompt = ({primitive, ...props})=>{
  const [editing, setEditing] = React.useState(false)
  const [confirmRemove, setConfirmRemove] = React.useState(false)
  const [deleteMessage, setDeleteMessage] = React.useState( "Are you sure you want to delete this prompt?" )
  const updateTitle = (newTitle )=>{
    primitive.title = newTitle
    return true
  }
  const textSize = props.editable ? "sm" : "xs"
  const itemCount = props.itemCount || 0
  const processed = props.processed || false
  const unprocessed = props.unprocessed || false
  const prompt = primitive
  const showAsEmpty = prompt.title === undefined || prompt.title === null || prompt.title.trim() === "" 
  const showEmptyAsPlaceholder = (props.editable === true) && !prompt.allowInput
  const processedNumbers = (processed + unprocessed ) > 1
  
  const handleRemove = async ()=>{
    await MainStore().removePrimitive( primitive )
    setConfirmRemove(false)
  }
  
  const promptConfirmRemove = ()=>{
    const evidence = primitive.primitives.allUniqueEvidence
    if( evidence.length > 0){
      setDeleteMessage(`Deletion of this prompt will also delete ${evidence.length} child items`)
    }

    setConfirmRemove(true)
  }

  return (
    <>
              <div key={prompt.id} className='w-full flex place-items-center mt-2 justify-between group'>
              <div key='summary' className={`flex text-${textSize} flex-0`}>
                  <p className={`p-1 pl-2 bg-gray-200 ${showAsEmpty && (!props.editable || !showEmptyAsPlaceholder) ? "rounded-md" : "rounded-l-md"} border border-gray-300`}> 
                      {showAsEmpty ? prompt.metadata.summaryEmpty : prompt.metadata.summary}
                  </p> 
                  {(showEmptyAsPlaceholder || !showAsEmpty) &&
                      <div className={`p-1 bg-white w-full rounded-r-md border border-gray-300 ${props.editable ? 'pr-2' : ''}`}> 
                          {props.editable
                            ?  <DebouncedInput
                                inline
                                size={textSize === "xs" ? "sm" : "sm"}
                                value={primitive.title}
                                placeholder="<Add term>"
                                className="w-full"
                                classNames={{
                                  input: `${(primitive.title || "").search(/\s/) == -1 ? "break-all" : "break-word"} grow text-${textSize} text-slate-700`,
                                }}
                                isReadOnly={props.showEdit ? !editing : false}
                                onFocus={()=>{
                                  if (props.showEdit && !editing) {
                                    setEditing(true)
                                  }
                                }}
                                onBlur={()=>{
                                  if (props.showEdit && editing) {
                                    setEditing(false)
                                  }
                                }}
                                onChange={(value)=>{
                                  if (!value) {
                                    return
                                  }
                                  updateTitle(value)
                                }}
                              />
                            : prompt.title
                            }
                      </div>
                  }
              </div>
              { props.editable
              ?  <div className='flex space-x-2 invisible group-hover:visible'>
                  <button
                    key='edit'
                      type="button"
                      onClick={()=>setEditing( !editing )}
                      className="flex h-8 w-8 flex-none items-center justify-center rounded-full bg-white text-gray-400 hover:bg-gray-100 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    {editing ? <CheckIcon className="h-5 w-5" aria-hidden="true" /> : <PencilIcon className="h-5 w-5" aria-hidden="true" />}
                  </button>
                  <button
                    key='delete'
                      type="button"
                      onClick={promptConfirmRemove}
                      className="flex h-8 w-8 flex-none items-center justify-center rounded-full bg-white text-gray-400 hover:bg-gray-100 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  > <TrashIcon className="h-5 w-5" aria-hidden="true" />
                  </button>
                </div>
              : <div className='flex w-fit grow-0 place-items-center'>
                  {processedNumbers 
                    ? <div className='flex w-12 grow-0 place-items-center border bg-amber-100'>
                        <div key='status_p' style={{width: `${processed / (processed + unprocessed)*100}%`}} className={`flex place-items-center justify-center h-4 bg-green-400`}></div>
                      </div>
                    : <div key='status' className={`ml-auto w-3 h-3 rounded-full border ${processed ? "bg-green-200" : 'bg-amber-200'}`}/>
                  }
                  {(itemCount > 0) && <p key='count' className="flex hover:bg-indigo-600 hover:text-white justify-end ml-0.5 px-1 py-0.5 rounded-md shrink-0 text-indigo-700 text-xs">{itemCount} items</p>}
                  </div>
              }
            </div>
      {confirmRemove && <ConfirmationPopup title="Confirm deletion" confirm={handleRemove} message={deleteMessage} cancel={()=>setConfirmRemove(false)}/>}
     </>)
}

function Aggregation({primitive, ...props}){
  useDataEvent("relationship_update", primitive.id)
  const [show, setShow] = useState(false)
  if( !primitive.metadata?.isAggregation ){return <></>}
  const subList = primitive.primitives.allUniqueEvidence
  if( subList.length === 0 ){return <></>}
  return (<>
    <p 
      onClick={()=>setShow(!show)}
      className={[
      "text-xs text-gray-500 mt-1 flex place-items-center hover:text-gray-800 cursor-pointer",
    ].join(" ")
    }>{subList.length} Items<ChevronRightIcon className={`w-2.5 h-2.5 ml-0.5 mt-0.5 ${show ? "rotate-90" : ""}`}/></p>
    {show && <div 
      className={[
        'border-l-2 space-y-2 pt-2 mt-1 ml-1 pl-2',
        ].join(" ")}>
      {subList.map((d)=>(
        <PrimitiveCard primitive={d} textSize='sm' titleAtBase showMeta compact disableHover showMenu menuProps={{showVisitPage:false, showUnlink: true}} relatedTo={primitive} />
      ))}
      </div>}
  </>)
}


export function SmallMeta(props){
  const metadata = props.metadata || props.primitive?.metadata

  return (
    <h3 className={[
        `flex text-slate-400 font-medium tracking-tight text-xs uppercase place-items-center`,
        props.inline ? "" : (props.showMeta === "small-top" ? "mb-2 border-b" : "mt-2")
        ].join(" ")}> 
              {metadata?.icon && <HeroIcon icon={metadata.icon} className='w-5 h-5 mr-1' strokeWidth={1}/>}
              {metadata?.title ?? `Generic ${props.primitive?.type}`}
            </h3>
  )
}

export function RelationshipItem({
  primitive,
  parent,
  onSelect,
  className,
  disableHover = true,
  showLink = true,
  textSize = 'xs',
  titleAtBase = true,
  ...props
}){
  const handleClick = props.onClick ?? (onSelect
    ? (event)=>{
        event?.stopPropagation?.()
        onSelect(event, primitive, parent)
      }
    : undefined)

  const mergedClassName = [
    'mx-1 mb-2',
    className
  ].filter(Boolean).join(' ') || undefined

  return (
    <PrimitiveCard
      {...props}
      variant={false}
      primitive={primitive}
      compact
      textSize={textSize}
      titleAtBase={titleAtBase}
      showLink={props.showLink ?? showLink}
      disableHover={props.disableHover ?? disableHover}
      className={mergedClassName}
      onClick={handleClick}
    />
  )
}

export function SelectableListItem({
  primitive,
  isActive,
  onSelect,
  activeClassName = '!bg-ccgreen-100 !border-ccgreen-200 !border',
  inactiveClassName = '!border !border-gray-50',
  className,
  showExpand = true,
  disableHover = true,
  ...props
}){
  const mergedClassName = [
    isActive ? activeClassName : inactiveClassName,
    className
  ].filter(Boolean).join(' ') || undefined

  const handleSelect = props.onClick ?? (onSelect ? () => onSelect(primitive) : undefined)
  const handleEnter = props.onEnter ?? (onSelect ? () => onSelect(primitive) : undefined)

  return (
    <PrimitiveCard
      {...props}
      variant={false}
      primitive={primitive}
      compact
      showExpand={props.showExpand ?? showExpand}
      disableHover={props.disableHover ?? disableHover}
      className={mergedClassName}
      onClick={handleSelect}
      onEnter={handleEnter}
    />
  )
}

export function SidePanel({
  className,
  showDetails = 'panel',
  panelOpen = true,
  showLink = true,
  major = true,
  showEdit = true,
  editing = true,
  ...props
}){
  const mergedClassName = ['mb-6', className].filter(Boolean).join(' ') || undefined

  return (
    <PrimitiveCard
      {...props}
      showDetails={showDetails}
      panelOpen={panelOpen}
      showLink={showLink}
      major={major}
      showEdit={showEdit}
      editing={editing}
      className={mergedClassName}
    />
  )
}

export function RelatedPrimitive({
  className,
  disableHover = true,
  showLink = true,
  compact = true,
  variant = false,
  ...props
}) {
  const mergedClassName = [className].filter(Boolean).join(' ') || undefined

  return (
    <PrimitiveCard
      {...props}
      variant={variant}
      compact={compact}
      disableHover={disableHover}
      showLink={showLink}
      className={mergedClassName}
    />
  )
}

export function PanelReference({
  className,
  showDetails = 'panel',
  showLink = true,
  showState = true,
  showUsers,
  ...props
}) {
  const resolvedShowUsers = showUsers === true ? 'panel' : showUsers
  const mergedClassName = [className].filter(Boolean).join(' ') || undefined

  return (
    <PrimitiveCard
      {...props}
      className={mergedClassName}
      showDetails={showDetails}
      showLink={showLink}
      showState={showState}
      showUsers={resolvedShowUsers}
    />
  )
}

export function EditableTitle({
  className = "bg-background",
  label = "Title",
  callback,
  primitive
}){
  return <DebouncedInput 
          label={label}
          value={primitive?.title} 
          className={className}
          onChange={value => {
            if(callback){
              return callback(value)
            }
            return primitive.title = value
          }}
        />

}
export function EditableHeader({
  className,
  compact = true,
  showEdit = true,
  disableHover = true,
  editing = true,
  variant = false,
  ...props
}) {
  const mergedClassName = ['w-full !bg-transparent', className]
    .filter(Boolean)
    .join(' ') || undefined

  return (
    <PrimitiveCard
      {...props}
      variant={variant}
      compact={compact}
      showEdit={showEdit}
      disableHover={disableHover}
      editing={editing}
      className={mergedClassName}
    />
  )
}

export function ModalPreview({
  className,
  disableHover = true,
  hideMenu = true,
  showId = false,
  variant = false,
  ...props
}) {
  const mergedClassName = [className].filter(Boolean).join(' ') || undefined

  return (
    <PrimitiveCard
      {...props}
      variant={variant}
      disableHover={disableHover}
      hideMenu={hideMenu}
      showId={showId}
      className={mergedClassName}
    />
  )
}

export function PrimaryInfo({
  className,
  showEdit = true,
  showId = false,
  major = true,
  disableHover = false,
  ...props
}) {
  const mergedClassName = [className].filter(Boolean).join(' ') || undefined

  return (
    <PrimitiveCard
      {...props}
      showEdit={showEdit}
      showId={showId}
      major={major}
      disableHover={disableHover}
      className={mergedClassName}
    />
  )
}

export function MatrixItem({
  className,
  compact = true,
  ...props
}) {
  const mergedClassName = [className].filter(Boolean).join(' ') || undefined

  return (
    <PrimitiveCard
      {...props}
      compact={compact}
      className={mergedClassName}
    />
  )
}

export function RelationshipPreview({
  className,
  inline = true,
  compact = true,
  disableHover = true,
  showLink = true,
  bg = 'bg-transparent',
  ...props
}) {
  const mergedClassName = ['w-fit', className]
    .filter(Boolean)
    .join(' ') || undefined

  return (
    <PrimitiveCard
      {...props}
      inline={inline}
      compact={compact}
      disableHover={disableHover}
      showLink={showLink}
      bg={bg}
      className={mergedClassName}
    />
  )
}

export function PrimitiveCard({primitive, className, showDetails, showUsers, showRelationships, major, disableHover, fields,...props}) {
  let ring = !disableHover
  let mainTextSize = props.textSize  || (props.compact ? 'sm' : 'md' )
  let margin = props.bigMargin ? (ring ? 'px-4 py-6' : 'px-2 py-3') : (ring ? 'px-2 py-3' : 'px-0.5 py-1')

  const [eventTracker, updateForEvent] = React.useReducer( (x)=>x+1, 0)
  const [editing, setEditing] = React.useState(false)
  const callbackId = React.useRef(null)
  React.useEffect(()=>{
    if( !props.noEvents ){
      const ids = [primitive.id, props.relationshipId].filter((d)=>d)
      callbackId.current = mainstore.registerCallback(callbackId.current, "set_title set_parameter set_field relationship_update", updateForEvent, ids )
      return ()=>{
        mainstore.deregisterCallback(callbackId.current )
      }
    }
  }, [])

  if( !major  && props.variant !== false){
    const variant = Variant({primitive, ...props, className, showDetails, showUsers, showRelationships, major, disableHover, fields})
    if( variant ){
      return variant
    }
  }



  let metaSummary
  let smallMeta
  let metadata
  if( props.showMeta){
    metadata = primitive.metadata
    if( metadata ){
      if( props.showMeta === "summary" ){
        metaSummary = <p className={`text-${mainTextSize} pl-1 font-medium`}>{metadata.summary}</p>
      }
      else if( props.showMeta !== "large" ){
        smallMeta = <SmallMeta {...props} metadata={metadata}/>
      }
    } 
  }
  let titleAtBase = fields || props.titleAtBase

  if( major ){
    margin = ""
    mainTextSize = "lg"
    ring = false
  }
  let withHero = props.enableHero && primitive.metadata?.title === "Venture"
  if( withHero ){
    mainTextSize = '2xl'
  }

  const updateTitle = (newTitle )=>{
    primitive.title = newTitle
    return true
  }
  
  if( fields ){
    fields = fields.map((d)=>d instanceof Object ? d.field : d)
  }

  const baseMenuProps = props.menuProps ?? {}
  const { buttonProps: providedMenuButtonProps = {}, className: menuClassName, ...restMenuProps } = baseMenuProps
  const mergedMenuButtonProps = {
    variant: 'light',
    isIconOnly: true,
    size: 'sm',
    ...providedMenuButtonProps,
    className: clsx('invisible group-hover:visible', menuClassName, providedMenuButtonProps.className),
  }

  let content = (fields && !fields.includes('title')) ? undefined :
      <>
        {props.editable === false
          ? 
            <p
              className={`${(primitive.title || "").search(/\s/) == -1 ? "break-all" : "break-word"} grow text-${mainTextSize} ${withHero ? "px-2 self-end text-slate-50 font-bold w-full" : "text-slate-700"}`}
            >
              {primitive.title}
            </p>
          : <DebouncedInput
            inline
            size={mainTextSize === "2xl" || mainTextSize === "xl" || mainTextSize === "lg" ? "md" : "sm"}
            value={primitive.title}
            placeholder={primitive.metadata?.placeholder ?? "Empty"}
            className="w-full"
            classNames={{
              input: `${(primitive.title || "").search(/\s/) == -1 ? "break-all" : "break-word"} grow text-${mainTextSize} ${withHero ? "px-2 self-end text-slate-50 font-bold" : "text-slate-700"}`,
            }}
            isReadOnly={props.showEdit ? !editing : false}
            onFocus={()=>{
              if (props.showEdit && !editing) {
                setEditing(true)
              }
            }}
            onBlur={()=>{
              if (props.showEdit && editing) {
                setEditing(false)
              }
            }}
            onChange={(value)=>{
              if (!value) {
                return
              }
              updateTitle(value)
            }}
          />
        }
        {props.showMenu &&
          <CardMenu
            primitive={primitive}
            callbackProcessor={props.callbackProcessor}
            relatedTo={props.relatedTo}
            buttonProps={mergedMenuButtonProps}
            {...restMenuProps}
          />
        }
        {(!props.compact && props.showEdit) &&
          <button
              type="button"
              onClick={ props.showEdit ? ()=>setEditing(!editing) : undefined}
              className="flex h-8 w-8 flex-none items-center justify-center rounded-full bg-white text-gray-400 hover:bg-gray-100 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
              {props.showEdit && !editing && <PencilIcon className="h-5 w-5" aria-hidden="true" />}
              {props.showEdit && editing && <CheckIcon className="h-5 w-5" aria-hidden="true" />}
          </button>
        }
        {(!props.compact && props.showLink) &&
          <button
              type="button"
              className="flex h-8 w-8 flex-none items-center justify-center rounded-full bg-white text-gray-400 hover:bg-gray-100 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
              {props.showLink &&  <Link to={`/item/${primitive.id}`}><ArrowTopRightOnSquareIcon className="h-5 w-5" aria-hidden="true" /></Link>}
          </button>
        }
        {(props.showSidebar) &&
          <button
              type="button"
              className="flex h-8 w-8 flex-none items-center justify-center rounded-full bg-white text-gray-400 hover:bg-gray-100 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
              <ArrowTopRightOnSquareIcon className="h-5 w-5" aria-hidden="true" onClick={()=>mainstore.sidebarSelect(primitive)}/>
          </button>
        }
      </>

  let header
  if(metadata && !smallMeta){
      header = <div className='flex'>
        <HeroIcon icon={metadata.icon} className={`${props.textSize === "sm" ? 'w-6 h-6' : 'w-10 h-10'} mr-2 shrink-0 grow-0 text-gray-400 ease-linear transition-colors  group-hover:text-gray-800`} strokeWidth={1}/>
        <div className='w-full'>
          <div className={`flex items-start justify-between ${props.compact ? "space-x-1" : "space-x-3"}`}>
            {metaSummary}
            {content}
          </div>
          {!titleAtBase && !props.hideTitle && <Title primitive={primitive} {...props} showMetadataTitle={props.showMetadataTitle === undefined ? true : props.showMetadataTitle} className='mt-1'/>} 
        </div>
      </div>

  }else{
    header = <>
              {!titleAtBase && !metadata && !props.hideTitle && <Title primitive={primitive} {...props}/>} 
              {smallMeta && props.showMeta === "small-top" && smallMeta}
              {content && 
                <div 
                  className={
                    withHero
                      ? "rounded-t-lg bg-gradient-to-br from-slate-900 to-slate-600 -mx-2 -mt-3 h-24 flex" 
                      : `flex items-start justify-between ${props.compact ? "space-x-1" : "space-x-3"} ${props.compact ? titleAtBase ? '' : 'mt-2' : 'mt-3'}`
                  }>
                {metaSummary}
                {content}
              </div>}
            </>
  }

  const handleEnter = (e)=>{
    if( e.key === "Enter"){
      e.preventDefault();
      e.stopPropagation()
      props.onEnter(primitive)
    }
  }

  const packedFields = fields ? fields.filter((d)=>d?.indexOf(",") >= 0).map((d)=>d.split(",")).flat() : undefined
  fields = fields ? fields.filter((d)=> (d !== "title") && (d?.indexOf(",") === -1)) : undefined

  let style = props.style || {}
  if( props.fixedWidth){
    style.minWidth = props.fixedWidth
    style.maxWidth = props.fixedWidth
  }
  if( props.fixedSize){
    style.minWidth = props.fixedSize
    style.maxWidth = props.fixedSize
    style.minHeight = props.fixedSize
    style.maxHeight = props.fixedSize
  }
  const showAsProcessing = primitive.processing?.pivot

  return (
    <div 
        onClick={props.onClick ? (e)=>props.onClick(e,primitive) : undefined }
        onKeyDown={props.onEnter ? handleEnter : undefined}
        tabIndex='0'
        id={props.fullId ? primitive.id : primitive.plainId}
        style={style}
        className={
        [
          "pcard group relative",
          props.bg ? props.bg : 'bg-content1 text-foreground',
          margin,
          props.flatBorder ? '' : 'rounded-lg',
          ring ? `focus:ring-2 focus:outline-none hover:ring-1 hover:ring-ccgreen-300 ${props.dragShadow ? "" : "hover:subtle-shadow-bottom"}` : '',
          props.border ? "shadow border border-default-200" : '',
          props.inline ? "flex space-x-2" : "",
          props.dragShadow ? "shadow-xl rotate-[-5deg]" : "",
          className].filter((d)=>d).join(' ')
        }>
        {props.showExpand && 

          <div 
            onClick={props.onEnter}
            className='absolute flex place-items-center justify-center right-0 top-0 mr-0.5 mt-0.5 w-5 h-5 text-default-400 invisible group-hover:visible hover:text-primary rounded-lg hover:bg-default-100 active:bg-primary active:text-primary-foreground'>
            <ExpandArrow 
              className='w-4 h-4'
            />
          </div>}
          {
            (primitive.type === "result" && primitive.referenceParameters?.hasImg)   && 
              <>
                <div className="bg-gray-200 h-[8.25rem] mb-2 w-full rounded-t-lg"/>
                <VFImage className="absolute top-0 left-0 object-cover h-36 w-full rounded-t-lg" src={`/api/image/${primitive.id}${primitive.imageCount ? `?${primitive.imageCount}` : ""}`}/>
              </>
          }
          {props.showImg 
            ?  
              <div className='w-full flex mb-2 text-lg'>
                <VFImage rounded shadow className="object-cover h-12 w-12 mr-2" src={`/api/image/${primitive.id}${primitive.imageCount ? `?${primitive.imageCount}` : ""}`}/>
                {header}
              </div>
            : header
          }
      {fields && (packedFields === undefined || fields.length > 0) &&
        <div className={[
          props.inline ? `flex items-start justify-between space-x-1 w-max` : ``,
          props.fieldsInline ? `flex -ml-1 space-x-1 py-2` : ``, 
          ].join(" ")}>
            <Parameters
              primitive={primitive}
              editable={false}
              noEvents={props.noEvents}
              mode={(props.inline || props.fieldsInline) ? "inline" : "form"}
              density="sm"
              showLabel={props.fieldsInline === true}
              asMain={true}
              fields={fields}
              className='!py-1'
            />
        </div>
      }
      {packedFields &&  (packedFields.length > 0) &&
        <div className='flex space-x-1 py-2'>
            <Parameters
              primitive={primitive}
              editable={false}
              noEvents={props.noEvents}
              mode="inline"
              density="sm"
              showLabel={false}
              asMain={true}
              fields={packedFields}
              className='!py-1'
            />
            {packedFields.includes('title') && <p className='text-sm my-1' >{primitive.title}</p>}
        </div>
      }

        {props.showOriginInfo && 
          <div className='flex items-start space-x-1'>
            <PrimitiveCard.Parameters
              editable={false}
              primitive={props.origin || primitive.origin}
              mode="inline"
              density="sm"
              showLabel={false}
              noEvents={props.noEvents}
              fields={props.showOriginInfo}
            />
          </div>}
        {showRelationships && <PrimitiveCard.Relationships primitive={primitive}/>}
          {props.showQuote === true && (primitive.quote || primitive.referenceParameters?.quote) && 
              <div className='w-full px-6 py-2 '>
                  <p className='pl-2 border-l-4 text-gray-500 italic text-sm line-clamp-6'><strong>Original text:</strong> {primitive.quote || primitive.referenceParameters?.quote}</p>
              </div>
          }
        {false && props.editState && <div className='w-full flex'><DropdownButton colorKey='colorBase' items={PrimitiveConfig.stateInfo[primitive.type]} setSelectedItem={(d)=>primitive.setField('state', Object.keys(PrimitiveConfig.stateInfo[primitive.type])[d] )} selected={primitive.state} portal small className='ml-auto'/></div>}
        {showDetails === true && <PrimitiveCard.Details primitive={primitive} editing={props.editing}/>}
        {showUsers === true && <PrimitiveCard.Users primitive={primitive}/>}
        {(showDetails === "panel" || showUsers === "panel") && <Panel title="More" collapsable={true} open={props.panelOpen}>
            {showDetails === "panel" && <PrimitiveCard.Details primitive={primitive}/>}
            {showUsers === "panel" && <PrimitiveCard.Users primitive={primitive}/>}
          </Panel>}
        {(props.showEvidence  === true) && <PrimitiveCard.Evidence primitive={primitive}/>}
        {(props.showEvidence  === "compact") && <PrimitiveCard.Evidence primitive={primitive} hideTitle={true} compact={true} aggregate={true}/>}
        {props.children}
        {props.editable !== false && titleAtBase && !props.hideTitle && <Title primitive={primitive} {...props} className={props.inline ? 'grow-0' : 'grow-0 mt-1'}/>}
        {smallMeta && props.showMeta !== "small-top" && smallMeta}
        {primitive._doingDiscovery && !primitive.discoveryDone && <div className='w-2 h-2 absolute bg-amber-500 rounded-lg right-1 top-1'/>}
        {primitive.openai_error && <div className='w-2 h-2 absolute bg-red-500 rounded-lg right-1 top-1'/>}
        {primitive.metadata?.isAggregation && <Aggregation primitive={primitive} {...props}/>}
        {props.showCategories && <div className='w-full flex flex-wrap'>
          {primitive.categories.map((category)=>(
            <CategoryCardPill primitive={category}/>
          ))}
        </div>}
        {showAsProcessing && ProcessingPane()}
    </div>
  )
}


function ProcessingBase({primitive, ...props}){
  useDataEvent("set_field", primitive?.id)
  
  let showAsProcessing = primitive?.processing && (primitive.processing.pivot || primitive.processing.enrich)

  if( !showAsProcessing ){
    if( primitive?.processing?.expanding){
      if( Object.values(primitive.processing.expanding).filter((d)=>d).length > 0){
        showAsProcessing = true
      }
    }
    if( primitive?.processing?.ai){
      if( Object.values(primitive.processing.ai).filter((d)=>d).length > 0){
        showAsProcessing = true
      }
    }
  }

  if( !showAsProcessing){
    return <></>
  }
  return (<div
      className="w-full h-2 absolute bottom-0 left-0 before:bg-gradient-to-r before:from-transparent before:via-ccgreen-600/100 before:to-transparent before:absolute before:bottom-0 before:left-0 before:w-full before:h-[6px] before:-translate-x-[90%] before:animate-[shimmer_4s_infinite]"
    >
  </div>)
}

function ProcessingPane(){
 return (<div className='absolute top-0 left-0 w-full h-full backdrop-blur-sm bg-white/40 place-items-center flex'>
            <Grid
              height="40%"
              color="#4fa94d"
              ariaLabel="grid-loading"
              radius="12.5"
              wrapperStyle={{}}
              wrapperClass="mx-auto"
              visible={true}
              />
          </div>)
}


function CardForList({primitive, fields, ...props}){
  const metadata = primitive.metadata

  let minor = metadata.renderConfig?.cards?.minor
  if( metadata.renderConfig?.cards?.fields ){
    fields = metadata.renderConfig?.cards?.fields
  }

  const showTitle = fields?.includes("title")
  const showIcon = fields?.includes("icon")
  
  
  let headerImg
  if( primitive.type === "entity" ){
    headerImg =  primitive?.referenceParameters?.hasBgImg
      ? `/api/image/${primitive.id}-background`
      : primitive.avatarImageUrl
  } else {
    headerImg = primitive.avatarImageUrl
  }

  if (props.smallCard) {
    const urlValue = primitive.referenceParameters?.url
    return (
      <div
        onClick={props.onClick ? ()=>props.onClick(undefined, primitive) : undefined}
        className='pcard group relative bg-content1 text-foreground rounded-lg focus:ring-2 focus:outline-none hover:ring-1 hover:ring-ccgreen-300 hover:subtle-shadow-bottom shadow border border-default-200 w-full min-w-0 overflow-hidden'
      >
        {headerImg && (headerImg !== null) && (
          <VFImage className="w-full h-20 object-cover" src={headerImg}/>
        )}
        <div className="px-2 py-2">
          <div className="flex items-start gap-2">
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-default-700">{primitive.title}</p>
              {urlValue && <p className="truncate text-xs text-default-400">{urlValue}</p>}
            </div>
            <CardMenu
              primitive={primitive}
              buttonProps={{ variant: 'light', isIconOnly: true, size: 'sm' }}
            />
          </div>
          <div className='mt-1 text-xs text-default-400'>{primitive.displayType} #{primitive.plainId}</div>
        </div>
      </div>
    )
  }

  return <div 
    onClick={props.onClick ? ()=>props.onClick(undefined, primitive) : undefined}
    className='pcard group relative bg-content1 text-foreground px-2 py-3 rounded-lg focus:ring-2 focus:outline-none hover:ring-1 hover:ring-ccgreen-300 hover:subtle-shadow-bottom shadow border border-default-200'>
      {headerImg && (headerImg !== null) &&
        <>
            <div className="bg-gray-200 h-[8.25rem] mb-2 w-full rounded-t-lg"/>
            <VFImage className="absolute top-0 left-0 object-cover h-36 w-full rounded-t-lg" src={headerImg}/>
        </>
      }
      <div className="border-b flex justify-between mb-2 pb-2 place-items-center w-full">
        {showIcon && <VFImage className="w-8 h-8 mr-2 object-contain my-auto shrink-0" src={`/api/image/${primitive.id}${primitive.imageCount ? `?${primitive.imageCount}` : ""}`} />}
        <DebouncedInput
            inline
            size="sm"
            value={primitive.title}
            className="w-full"
            classNames={{ input: "grow" }}
            onChange={(value)=>{
              if (!value) {
                return
              }
              primitive.title = value
            }}
            />
          {metadata && metadata.icon && <HeroIcon icon={metadata.icon} className='w-12 h-12 shrink-0' strokeWidth={0.8}/>}
        </div>
        <div className='flex flex-col px-1 space-y-1.5 text-sm mb-2'>
          {(fields ?? []).filter(d=>primitive.referenceParameters[d] && metadata?.parameters[d]).map(field=>(
            <div className='felx flex-col'>
              <p className='text-slate-400 text-xs font-semibold uppercase'>{metadata.parameters[field].title}</p>
              <RenderItem
                primitive={primitive}
                item={{...metadata.parameters[field], value:primitive.referenceParameters[field], key: field}}
                density="sm"
                mode="form"
                showLabel={true}
                leftAlign
                border={false}
              />
            </div>
          ))}
        </div>
        {minor && <div className='flex flex-col px-1 space-y-1.5 text-xs mt-1 mb-2 text-slate-400'>
          {minor.filter(d=>primitive.referenceParameters[d] && metadata?.parameters[d]).map(field=>(
              <RenderItem
                primitive={primitive}
                minor
                editable={false}
                item={{...metadata.parameters[field], value:primitive.referenceParameters[field], key: field}}
                density="sm"
                mode="form"
                showLabel={true}
                leftAlign
                border={false}
              />
          ))}
        </div>}
        <PrimitiveCard.Evidence primitive={primitive} hideTitle={true} compact={true} aggregate={true}/>
        <div className='w-full justify-between flex '>
          <p className='mt-2 text-slate-400 text-xs'>{primitive.displayType} #{primitive.plainId}</p>
          <CardMenu
            primitive={primitive}
            buttonProps={{ variant: 'light', isIconOnly: true, size: 'sm' }}
          />
        </div>
    </div>
}
function Outputs({primitive}){
    const values = primitive.outputs
    const outputPins = primitive.outputPinsWithStatus

    let pins = Object.keys(primitive.outputPins).reduce((a,c)=>{a.push({...outputPins[c], id: c}); return a},[])
    
    if(outputPins){
      let removeImport = true
      if( outputPins.impout?.connected){

        const primOuts = primitive.itemsForProcessing
        if( primOuts.length > 0){
          values.impout = {
            config: "primitive",
            data: primOuts
          }
          removeImport = false
        }
      }
      if( removeImport){
        pins = pins.filter(d=>d.id !== "impout")
      }
    }
    
    return RenderPins({primitive, values, pins})
}
function Inputs({primitive}){
    const pins = primitive.outputPins
    return RenderPins({primitive, pins})
}
function InputPins({primitive, dataForNewInstance, newInstanceCallback, updateMissing, pins}){
  if( !(primitive.type === "flowinstance" || (primitive.type === "flow" && newInstanceCallback))){
    return <></>
  }
  pins = pins ?? primitive.getConfig?.inputPins
  return EditablePins( {primitive, pins, dataForNewInstance, newInstanceCallback, updateMissing})
}
function ControlPins({primitive}){
  if( primitive.type !== "flow"){
    return <></>
  }
  const pins = primitive.getConfig?.controlPins ?? {}
  return EditablePins( {primitive, pins})
}
const isPinOptional = (pinConfig = {}) => {
  if (typeof pinConfig.optional === "boolean") {
    return pinConfig.optional
  }
  if (typeof pinConfig.required === "boolean") {
    return !pinConfig.required
  }
  return true
}

const isValueEmpty = (value) => {
  if (value === null || value === undefined) {
    return true
  }
  if (typeof value === "string") {
    return value.trim() === ""
  }
  if (Array.isArray(value)) {
    return value.length === 0
  }
  return false
}

function EditablePins({primitive, pins, dataForNewInstance, newInstanceCallback, updateMissing}){
    const pinItems = buildPinItems({ primitive, pins, dataForNewInstance })
    const pinsToRender = pinItems.map((entry) => entry.pinName)
    const [missing, setMissing ]= useState(()=>{
      const initialMissing = pinsToRender.reduce((acc, pinKey)=>{
        const config = pins[pinKey]
        if( config.split){
          return acc
        }
        const optional = isPinOptional(config)
        const existingValue = dataForNewInstance && pinKey in dataForNewInstance
          ? dataForNewInstance[pinKey]
          : primitive?.referenceParameters?.[pinKey]
        acc[pinKey] = !optional && isValueEmpty(existingValue)
        return acc
      }, {})
      if( updateMissing ){
        updateMissing( initialMissing )
      }
      return initialMissing
    })

    const renderedRows = []

    pinItems.forEach((entry)=>{
        const { pinName, pinConfig } = entry
        if( entry.split){
          renderedRows.push(
            <div
              key={`split-${pinName}`}
              className={clsx(
                "bg-slate-50 px-5 py-3 text-xs font-semibold uppercase tracking-wide text-slate-400",
                renderedRows.length > 0 && "border-t border-slate-200/70"
              )}
            >
              {pinConfig.title ?? pinConfig.name ?? ""}
            </div>
          )
          return
        }

        const optional = isPinOptional(pinConfig)
        const controlId = `pin-${primitive?.id ?? "new"}-${pinName}`
        const helperText = pinConfig.description ?? pinConfig.helperText ?? ""
        let control = RenderItem({
            primitive,
            leftAlign: true,
            embedded: true,
            callback: (value)=>{
              const required = !optional
              const newMissing = {
                  ...missing,
                  [pinName]: required && isValueEmpty(value)
              }
              setMissing(newMissing)
              if( updateMissing ){
                updateMissing( newMissing )
              }
              if( newInstanceCallback ){
                return newInstanceCallback(pinName, value)
              }else{
                return primitive.setParameter(pinName, value)
              }
            },
            item: entry.item,
          })

        if( React.isValidElement(control) ){
          const existingClassNames = control.props.classNames ?? {}
          control = React.cloneElement(control, {
            id: control.props.id ?? controlId,
            size: control.props.size ?? "lg",
            radius: control.props.radius ?? "lg",
            labelPlacement: control.props.labelPlacement ?? "outside",
            className: clsx("w-full", control.props.className),
            classNames: {
              ...existingClassNames,
              mainWrapper: clsx("w-full", existingClassNames.mainWrapper),
              inputWrapper: clsx(
                "shadow-none border border-slate-200 bg-white px-3",
                missing[pinName] ? "border-rose-300 focus:border-rose-400" : "hover:border-slate-300 focus:border-blue-500",
                existingClassNames.inputWrapper
              ),
            },
          })
        }

        renderedRows.push(
          <div
            key={pinName}
            className={clsx(
              "px-5 py-4 transition-colors",
              renderedRows.length > 0 && "border-t border-slate-200/70",
              missing[pinName] ? "bg-rose-50/40 focus-within:bg-rose-50" : "bg-white focus-within:bg-slate-50"
            )}
          >
            <div className="flex flex-col gap-3">
              <label htmlFor={controlId} className="flex flex-wrap items-center gap-2 text-sm font-medium text-slate-800">
                <span>{pinConfig.name}</span>
                {!optional && <span className="text-rose-500">*</span>}
                {optional && <span className="ml-auto text-xs font-medium text-slate-400 normal-case">Optional</span>}
                {!optional && missing[pinName] && (
                  <span className="ml-auto text-xs font-semibold uppercase tracking-wide text-rose-500">Required</span>
                )}
              </label>
              {helperText && (
                <p className="text-xs leading-relaxed text-slate-500">
                  {helperText}
                </p>
              )}
              <div className="flex flex-col gap-1">
                {control}
              </div>
            </div>
          </div>
        )
      })

    return (
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        {renderedRows}
      </div>
    )
}
function RenderPinValues({values, pin}){
    const pinValue = values[pin.id]
    if( pinValue ){
        const renderAs = pinValue.config
        if( renderAs === "string_list"){
            return <p>{pinValue.data.join(", ")}</p>
        }else if(renderAs === "string"){
            return <p>{pinValue.data}</p>
        }else if(renderAs === "object_list"){
          return <ul>{pinValue.data.map(d=>Object.keys(d).map(k=><li>{k}:{d[k]}</li>))}</ul>
        }else if(renderAs === "primitive"){
            return <div className='flex flex-col space-y-2'>
                {pinValue.data.map(d=><PrimitiveCard.MatrixItem primitive={d}/>)}
              </div>
        }
    }
    return <></>
}
function RenderPins({primitive, values, pins}){
  return <DescriptionList inContainer={true}>
    {pins.map(d=><>
        <DescriptionTerm inContainer={true}>{d.name}</DescriptionTerm>
        <DescriptionDetails inContainer={true}>{RenderPinValues({values, pin: d})}</DescriptionDetails>
    </>)}

  </DescriptionList>
};

PrimitiveCard.ProcessingBase = ProcessingBase
PrimitiveCard.Variant = Variant
PrimitiveCard.Details = Details
PrimitiveCard.Questions = Questions
PrimitiveCard.Categories = Categories
PrimitiveCard.Parameters = Parameters
PrimitiveCard.Users = Users
PrimitiveCard.Relationships = Relationships
PrimitiveCard.Banner = Banner
PrimitiveCard.CompactBanner = CompactBanner
PrimitiveCard.Title = Title
PrimitiveCard.Hero = Hero
PrimitiveCard.Evidence = Evidence
PrimitiveCard.Entity = Entity
PrimitiveCard.EvidenceList = EvidenceList
PrimitiveCard.CardMenu = CardMenu
PrimitiveCard.SmallMeta = SmallMeta
PrimitiveCard.RelationshipItem = RelationshipItem
PrimitiveCard.SelectableListItem = SelectableListItem
PrimitiveCard.SidePanel = SidePanel
PrimitiveCard.RelatedPrimitive = RelatedPrimitive
PrimitiveCard.PanelReference = PanelReference
PrimitiveCard.EditableHeader = EditableHeader
PrimitiveCard.EditableTitle = EditableTitle
PrimitiveCard.ModalPreview = ModalPreview
PrimitiveCard.PrimaryInfo = PrimaryInfo
PrimitiveCard.MatrixItem = MatrixItem
PrimitiveCard.RelationshipPreview = RelationshipPreview
PrimitiveCard.ListCard = CardForList
PrimitiveCard.OutputPins = Outputs
PrimitiveCard.InputPins = InputPins
PrimitiveCard.ControlPins = ControlPins
